import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from './components/Header/Header';
import Popup from './components/Popup/Popup';
import HeroSection from './components/HeroSection';
import AdvantagesSection from './components/AdvantagesSection/AdvantagesSection';
import PricesSection from './components/PricesSection/PricesSection';
import DynamicSEO from './components/SEO/DynamicSEO';
import { fetchApi } from './utils/api';
import type { CardData } from './components/PricesSection/PricesSection';
import './App.css';

// Lazy loading — секции ниже fold
const TryFreeSection = lazy(() => import('./components/TryFreeSection/TryFreeSection'));
const WhoIsClientSection = lazy(() => import('./components/WhoIsClientSection/WhoIsClientSection'));
const ReviewsSection = lazy(() => import('./components/ReviewsSection/ReviewsSection'));
const FaqSection = lazy(() => import('./components/FaqSection/FaqSection'));
const FirstTimeSection = lazy(() => import('./components/FirstTimeSection/FirstTimeSection'));
const Footer = lazy(() => import('./components/Footer/Footer'));
const BotFeaturesSection = lazy(() => import('./components/BotFeaturesSection'));

// Lazy loading для страниц, которые не на главной
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./components/TermsConditions/TermsConditions'));
const PartnersPage = lazy(() => import('./components/PartnersPage/PartnersPage'));

const icons = [
  'https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/router-fill.svg',
  'https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/lock-fill.svg',
  'https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/shield-lock-fill.svg',
  'https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/cloud-fill.svg',
  'https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/key-fill.svg',
  'https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/hdd-network-fill.svg',
  'https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/globe2.svg',
  'https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/wifi.svg',
  'https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/server.svg',
  'https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/ethernet.svg',
  'https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/incognito.svg',
  'https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/shield-check.svg',
  'https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/shield-fill-check.svg',
  'https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/fingerprint.svg',
  'https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/cloud-check-fill.svg',
  'https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/reception-4.svg',
];

const iconColors = ['red', 'blue', 'black', 'gray'];

interface IconData {
  src: string;
  top: string;
  left: string;
  duration: string;
  delay: string;
  colorClass: string;
}

function generateIconData(count: number): IconData[] {
  return Array.from({ length: count }).map(() => {
    const src = icons[Math.floor(Math.random() * icons.length)];
    const top = Math.random() * 100 + '%';
    const left = Math.random() * 100 + '%';
    const duration = (Math.random() * 30 + 25).toFixed(2) + 's';
    const delay = '-' + (Math.random() * 30).toFixed(2) + 's';
    const colorClass = iconColors[Math.floor(Math.random() * iconColors.length)];

    return {
      src,
      top,
      left,
      duration,
      delay,
      colorClass,
    };
  });
}

function HomePage({ onOpenPopup }: { onOpenPopup: () => void }) {
  const [floatingIcons] = useState<IconData[]>(() => {
    const isMobile = window.innerWidth <= 768;
    return generateIconData(isMobile ? 8 : 20);
  });
  const [showIcons, setShowIcons] = useState(false);
  const [cards, setCards] = useState<CardData[]>([]);
  const [cardsLoading, setCardsLoading] = useState(true);

  useEffect(() => {
    const loadCards = async () => {
      try {
        const result = await fetchApi<{ data: CardData[]; is_success: boolean }>('/api/public/landing/cards');
        if (result.is_success && result.data) {
          setCards(result.data);
        }
      } catch (error) {
        // silent fail
      } finally {
        setCardsLoading(false);
      }
    };
    loadCards();

    // Defer floating icons to not block LCP
    const timer = setTimeout(() => setShowIcons(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="animatedBackground">
      <DynamicSEO cards={cards} />
      {showIcons && (
        <div className="iconsContainer">
          {floatingIcons.map((icon, index) => (
            <img
              key={index}
              src={icon.src}
              alt=""
              crossOrigin="anonymous"
              loading="lazy"
              decoding="async"
              className={`floatingIcon ${icon.colorClass}`}
              style={{
                top: icon.top,
                left: icon.left,
                animationDuration: icon.duration,
                animationDelay: icon.delay,
              }}
            />
          ))}
        </div>
      )}

      <div style={{ position: 'relative', zIndex: 999 }}>
        <Header onOpenPopup={onOpenPopup} />
        <HeroSection onOpenPopup={onOpenPopup} cards={cards} loading={cardsLoading} />
        <AdvantagesSection />
        <PricesSection onOpenPopup={onOpenPopup} cards={cards} loading={cardsLoading} />
        <Suspense fallback={null}>
          <TryFreeSection onOpenPopup={onOpenPopup} />
          <WhoIsClientSection />
          <BotFeaturesSection />
          <ReviewsSection />
          <FaqSection />
          <FirstTimeSection onOpenPopup={onOpenPopup} />
          <Footer onOpenPopup={onOpenPopup} />
        </Suspense>
      </div>
    </div>
  );
}

// Scroll to top on route change
function ScrollManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { i18n } = useTranslation();

  const handleOpenPopup = () => setIsPopupOpen(true);
  const handleClosePopup = () => setIsPopupOpen(false);

  // Update <html lang> attribute when language changes (SEO)
  useEffect(() => {
    document.documentElement.lang = i18n.language.startsWith('ru') ? 'ru' : 'en';
  }, [i18n.language]);

  return (
    <Router>
      <ScrollManager />
      <Suspense fallback={<div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>}>
        <Routes>
          <Route path="/" element={<HomePage onOpenPopup={handleOpenPopup} />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy onOpenPopup={handleOpenPopup} />} />
          <Route path="/terms" element={<TermsConditions onOpenPopup={handleOpenPopup} />} />
          <Route path="/partners" element={<PartnersPage onOpenPopup={handleOpenPopup} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>

      {isPopupOpen && <Popup onClose={handleClosePopup} />}
    </Router>
  );
}

export default App;